import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useRouteError } from 'react-router';
import { P2GApp, P2GAppConfiguration } from '@parcel2go/npm-p2g-app';
import { BasketProvider } from '~/providers/basketContext';
import ScrollToTop from './scroll';
import { P2GBox, P2GButton, P2GGrid, P2GText } from '@parcel2go/core-ui';
import * as Sentry from '@sentry/react';
import Loading from '~/components/core/progress/loading';
import { capturePromiseException } from '~/lib/metrics';
import { WorkflowProvider } from '~/providers/workflowContext';
import {
  ANALYTICS_EDGE_API,
  APP,
  CONTENT_API,
  ENVIRONMENT,
  GTM_ENABLED,
  LOCALES_PATH,
  QUERY_API,
  VERSION,
  WSS,
  LOCAL_TENANT_OVERRIDE,
  TRANSLATION_NAMESPACES_ARRAY
} from '~/lib/constants';
import ConnectionStatus from '~/components/connection-status/connection-status';
import EventEmitter from 'events';

export function Component() {
  const events = useRef<EventEmitter>(new EventEmitter());
  const [itemsInBasket, setItemsInBasket] = useState<number>(0);

  const settings: P2GAppConfiguration = useMemo(
    () => ({
      app: APP,
      appVersion: VERSION,
      contentApi: CONTENT_API,
      environment: ENVIRONMENT,
      gtmEnabled: GTM_ENABLED,
      localesPath: LOCALES_PATH,
      wss: WSS,
      overrideNumberOfItemsInBasket: itemsInBasket,
      queryApi: QUERY_API,
      headerVariant: 'SIMPLE',
      footerVariant: 'SIMPLE',
      tenantOverride: LOCAL_TENANT_OVERRIDE,
      translationNamespaces: TRANSLATION_NAMESPACES_ARRAY,
      segmentation: true,
      preventMissingKeyUpdates: false // Set to true if you dont want translations to be updated automatically
    }),
    [itemsInBasket]
  );

  useEffect(() => {
    const onBasketCountChanged = (items: number) => {
      if (items !== itemsInBasket) {
        setItemsInBasket(items);
      }
    };

    const e = events.current;

    e.on('basket_count_updated', onBasketCountChanged);

    return () => {
      e.off('basket_count_updated', onBasketCountChanged);
    };
  }, [events, itemsInBasket]);

  return (
    <ScrollToTop>
      <P2GApp settings={settings} analyticsProps={{ endpoint: ANALYTICS_EDGE_API }}>
        <BasketProvider events={events.current}>
          <WorkflowProvider>
            <Suspense fallback={<Loading />}>
              <P2GBox
                sx={(x) => ({
                  backgroundColor: x.palette?.grey?.A100,
                  minHeight: '50vh',
                  paddingTop: x.spacing(2),
                  paddingBottom: x.spacing(2)
                })}
              >
                <Outlet />
              </P2GBox>
              <ConnectionStatus />
            </Suspense>
          </WorkflowProvider>
        </BasketProvider>
      </P2GApp>
    </ScrollToTop>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();

  useEffect(() => {
    if (error) {
      capturePromiseException(error);
    }
  }, [error]);

  return (
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
      <P2GBox p={6}>
        <P2GGrid container direction="column" alignItems="center" spacing={2}>
          <P2GGrid item>
            <P2GText variant="h3">Oops!</P2GText>
          </P2GGrid>
          <P2GGrid item>
            <P2GText variant="h4" component="p" textAlign="center">
              Something went a bit wrong.
              <br />
              We sincerely apologise for any inconvenience caused.
            </P2GText>
          </P2GGrid>

          <P2GGrid item>
            <P2GButton onClick={() => (window.location.href = '/')}>Back to homepage</P2GButton>
          </P2GGrid>
        </P2GGrid>
      </P2GBox>
    </Sentry.ErrorBoundary>
  );
}

ErrorBoundary.displayName = 'Root Error Boundary';
